import React, { useEffect, useRef, useState } from "react";
import styles from "./login.module.scss";
import { Path } from "enums";
import { redirectTo } from "utils";

const ALLOWED_CREDENTIALS = {
  orgId: "1234",
  email: "test@nexus.com",
  password: "password",
  otp: "111111",
};

const toBase64Url = (value) => {
  return window
    .btoa(JSON.stringify(value))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

const createMockJwt = (payload) => {
  const header = { alg: "HS256", typ: "JWT" };
  return `${toBase64Url(header)}.${toBase64Url(payload)}.signature`;
};

const LoginModule = () => {
  const [form, setForm] = useState({
    orgId: "",
    email: "",
    password: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [otpErrorMessage, setOtpErrorMessage] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [timeLeftInSeconds, setTimeLeftInSeconds] = useState(300);
  const otpInputRef = useRef(null);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prevForm) => ({ ...prevForm, [name]: value }));
    setErrorMessage("");
  };

  const isAllowedLogin = () => {
    return (
      form.orgId.trim() === ALLOWED_CREDENTIALS.orgId &&
      form.email.trim() === ALLOWED_CREDENTIALS.email &&
      form.password === ALLOWED_CREDENTIALS.password
    );
  };

  useEffect(() => {
    if (!showOtpModal || timeLeftInSeconds <= 0) {
      return undefined;
    }

    const timerId = setInterval(() => {
      setTimeLeftInSeconds((previousValue) => Math.max(previousValue - 1, 0));
    }, 1000);

    return () => clearInterval(timerId);
  }, [showOtpModal, timeLeftInSeconds]);

  useEffect(() => {
    if (showOtpModal && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [showOtpModal]);

  const formatTimeLeft = () => {
    const minutes = Math.floor(timeLeftInSeconds / 60);
    const seconds = String(timeLeftInSeconds % 60).padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const handleOtpChange = (event) => {
    const digitsOnly = event.target.value.replace(/\D/g, "").slice(0, 6);
    setOtpValue(digitsOnly);
    setOtpErrorMessage("");
  };

  const openOtpModal = () => {
    setOtpValue("");
    setOtpErrorMessage("");
    setTimeLeftInSeconds(300);
    setShowOtpModal(true);
  };

  const closeOtpModal = () => {
    setShowOtpModal(false);
    setOtpValue("");
    setOtpErrorMessage("");
    setTimeLeftInSeconds(300);
  };

  const completeLogin = () => {
    const nowInSeconds = Math.floor(Date.now() / 1000);
    const accessToken = createMockJwt({
      email: ALLOWED_CREDENTIALS.email,
      orgId: ALLOWED_CREDENTIALS.orgId,
      exp: nowInSeconds + 60 * 60 * 24,
    });
    const refreshToken = createMockJwt({
      type: "refresh",
      exp: nowInSeconds + 60 * 60 * 24 * 7,
    });

    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    localStorage.setItem("email", ALLOWED_CREDENTIALS.email);

    redirectTo(Path.Slash);
  };

  const handleLogin = (event) => {
    event.preventDefault();

    if (!isAllowedLogin()) {
      setErrorMessage("Invalid credentials. Use the demo credentials shown below.");
      return;
    }

    openOtpModal();
  };

  const handleOtpVerification = () => {
    if (timeLeftInSeconds <= 0) {
      setOtpErrorMessage("OTP expired. Refresh the page to try again.");
      return;
    }

    if (otpValue !== ALLOWED_CREDENTIALS.otp) {
      setOtpErrorMessage("Invalid OTP. Use the demo OTP shown below.");
      return;
    }

    completeLogin();
  };

  const otpSlots = Array.from({ length: 6 }, (_, index) => otpValue[index] || "");
  const activeOtpIndex = Math.min(otpValue.length, 5);

  return (
    <div className={styles.page}>
      <div className={styles.ambient}>
        <div className={styles.orbTop} />
        <div className={styles.orbBottom} />
      </div>

      <div className={styles.loginCard}>
        <div className={styles.header}>
          <div className={styles.brandIconWrap}>
            <div className={styles.brandIcon}>
              <svg viewBox="0 0 24 24" fill="none" className={styles.brandIconSvg}>
                <path
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          <h1 className={styles.title}>Nexus Recording</h1>
          <p className={styles.subtitle}>Command Center Portal</p>
        </div>

        <form className={styles.form} onSubmit={handleLogin}>
          <div className={styles.fieldGroup}>
            <label htmlFor="orgId">Organization ID</label>
            <input
              id="orgId"
              name="orgId"
              type="text"
              placeholder="Enter organization ID"
              value={form.orgId}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="Enter email address"
              value={form.email}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              placeholder="Enter password"
              value={form.password}
              onChange={handleInputChange}
              required
            />
          </div>

          {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

          <button type="submit" className={styles.submitButton}>
            Continue
          </button>
        </form>

        <div className={styles.demoCredentials}>
          Demo credentials: 1234 / test@nexus.com / password
        </div>
      </div>

      {showOtpModal && (
        <div className={styles.otpDialogOverlay} role="presentation" onClick={closeOtpModal}>
          <div
            className={styles.otpDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="otp-dialog-title"
            aria-describedby="otp-dialog-description"
            onClick={(event) => event.stopPropagation()}
          >
            <button type="button" className={styles.closeButton} onClick={closeOtpModal}>
              x
            </button>

            <div className={styles.otpHeader}>
              <h2 id="otp-dialog-title">Verify Your Identity</h2>
              <p id="otp-dialog-description">
                We've sent a 6-digit code to {ALLOWED_CREDENTIALS.email}
              </p>
            </div>

            <div className={styles.otpContent}>
              <div className={styles.otpInputContainer}>
                <div className={styles.otpSlotsGroup}>
                  {otpSlots.map((digit, index) => (
                    <div
                      key={`otp-slot-${index + 1}`}
                      className={`${styles.otpSlot} ${index === activeOtpIndex ? styles.otpSlotActive : ""}`}
                    >
                      {digit}
                    </div>
                  ))}
                </div>
                <input
                  ref={otpInputRef}
                  className={styles.otpHiddenInput}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={otpValue}
                  onChange={handleOtpChange}
                />
              </div>

              <div className={styles.otpMeta}>
                <div>
                  Time remaining: <span className={styles.otpTime}>{formatTimeLeft()}</span>
                </div>
                <p>Didn't receive the code? Wait for timer to resend.</p>
              </div>

              {otpErrorMessage && <div className={styles.errorMessage}>{otpErrorMessage}</div>}

              <button
                type="button"
                className={styles.submitButton}
                onClick={handleOtpVerification}
                disabled={otpValue.length !== 6}
              >
                Verify &amp; Continue
              </button>

              <div className={styles.otpDemo}>Demo OTP: 111111</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginModule;
